import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Trash2, Save, Plus, Upload, Star, Youtube, Download, GripVertical, Award } from "lucide-react";

const MOODS = ["recovery", "angry", "sad", "happy", "reminder", "reflection"];
const PAIRABLE_TOOLKITS = [
  { id: "first_72_hours", label: "First 72 Hours" },
  { id: "mapping_the_cycle", label: "Mapping the Cycle" },
  { id: "inner_weather", label: "The Inner Weather" },
  { id: "standing_in_the_storm", label: "Standing in the Storm" },
];

const emptyTrack = () => ({
  title: "",
  artist: "",
  mood: "recovery",
  note: "",
  is_sample: false,
  is_featured: false,
  paired_toolkits: [],
});

const AdminSoundtrack = () => {
  const { user } = useAuth();
  const nav = useNavigate();
  const [isAdmin, setIsAdmin] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [newTrack, setNewTrack] = useState(emptyTrack());
  const [bulkText, setBulkText] = useState("");
  const [saving, setSaving] = useState(false);
  const [ytUrl, setYtUrl] = useState("");
  const [ytClearFirst, setYtClearFirst] = useState(false);
  const [importing, setImporting] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [orderDirty, setOrderDirty] = useState(false);

  useEffect(() => {
    if (!user) { nav("/login"); return; }
    api.get("/admin/me").then(({ data }) => {
      setIsAdmin(data.is_admin);
      if (data.is_admin) refresh();
    }).catch(() => setIsAdmin(false));
    // eslint-disable-next-line
  }, [user]);

  const refresh = async () => {
    try {
      const { data } = await api.get("/soundtrack");
      setTracks(data.tracks || []);
    } catch {
      try {
        const { data } = await api.get("/soundtrack/preview");
        setTracks(data.sample_tracks.map((t, i) => ({ ...t, id: `preview-${i}` })));
      } catch {}
    }
  };

  const togglePairing = (arr, id) =>
    arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id];

  const addTrack = async () => {
    if (!newTrack.title.trim() || !newTrack.artist.trim()) {
      toast.error("Title and artist are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/soundtrack/tracks", {
        ...newTrack,
        note: newTrack.note.trim() || null,
      });
      setNewTrack(emptyTrack());
      await refresh();
      toast.success("Track added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add track");
    } finally { setSaving(false); }
  };

  const updateTrack = async (t) => {
    try {
      await api.put(`/admin/soundtrack/tracks/${t.id}`, {
        title: t.title,
        artist: t.artist,
        mood: t.mood,
        note: t.note || null,
        is_sample: !!t.is_sample,
        is_featured: !!t.is_featured,
        paired_toolkits: t.paired_toolkits || [],
      });
      toast.success("Saved");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const importFromYT = async () => {
    if (!ytUrl.trim()) { toast.error("Paste a playlist URL"); return; }
    setImporting(true);
    try {
      const { data } = await api.post("/admin/soundtrack/import-yt", {
        playlist_url: ytUrl.trim(),
        clear_existing: ytClearFirst,
      });
      toast.success(`Imported ${data.added} · Duplicates skipped: ${data.skipped_duplicates}`);
      setYtUrl("");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setImporting(false); }
  };

  const downloadCsv = async () => {
    try {
      const token = localStorage.getItem("wwr_token");
      const res = await fetch(`${API}/admin/soundtrack/tracks/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "soundtrack.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch { toast.error("Could not download"); }
  };

  const uploadCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    try {
      const { data } = await api.post("/admin/soundtrack/tracks/import-csv", { csv_text: text });
      toast.success(`Added ${data.added} · Updated ${data.updated} · Skipped ${data.skipped}`);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "CSV import failed");
    }
    e.target.value = "";
  };

  // Drag-and-drop reorder
  const onDragStart = (i) => setDragIndex(i);
  const onDragOver = (e, i) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === i) return;
    const next = [...tracks];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(i, 0, moved);
    setTracks(next);
    setDragIndex(i);
    setOrderDirty(true);
  };
  const onDragEnd = () => setDragIndex(null);

  const saveOrder = async () => {
    try {
      await api.post("/admin/soundtrack/tracks/reorder", {
        ordered_ids: tracks.map((t) => t.id),
      });
      toast.success("Order saved");
      setOrderDirty(false);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save order failed");
    }
  };

  const deleteTrack = async (id) => {
    if (!window.confirm("Delete this track?")) return;
    try {
      await api.delete(`/admin/soundtrack/tracks/${id}`);
      await refresh();
      toast.success("Deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const bulkImport = async () => {
    if (!bulkText.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/admin/soundtrack/tracks/bulk", { text: bulkText });
      toast.success(`Added ${data.added} · Skipped ${data.skipped}`);
      setBulkText("");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk import failed");
    } finally { setSaving(false); }
  };

  if (isAdmin === null) return <div className="p-12 text-center text-teal-200/70">Loading…</div>;
  if (!isAdmin) {
    return (
      <div className="fade-in max-w-2xl mx-auto px-6 py-24 text-center">
        <h1 className="font-display text-3xl mb-3">Not an admin</h1>
        <p className="text-neutral-400">Your account isn't authorized to manage the soundtrack.</p>
      </div>
    );
  }

  const sampleCount = tracks.filter((t) => t.is_sample).length;

  return (
    <div className="fade-in max-w-5xl mx-auto px-6 py-16" data-testid="admin-soundtrack">
      <div className="text-xs uppercase tracking-[0.24em] text-teal-300/80 mb-4">Admin</div>
      <h1 className="font-display text-4xl mb-2">Soundtrack manager</h1>
      <p className="text-neutral-400 mb-6">Add, edit, annotate, and pair tracks with your written toolkits.</p>

      <div className="grid sm:grid-cols-3 gap-3 mb-10">
        <StatCard label="Total tracks" value={tracks.length} />
        <StatCard label="Marked as sample" value={sampleCount} accent />
        <StatCard label="Paired to a toolkit" value={tracks.filter((t) => (t.paired_toolkits || []).length > 0).length} />
      </div>

      {/* YouTube Music import */}
      <div className="card-surface p-6 mb-6" data-testid="admin-yt-import">
        <h2 className="font-display text-xl mb-2 flex items-center gap-2"><Youtube size={16} className="text-red-400" /> Import from YouTube Music</h2>
        <p className="text-xs text-neutral-500 mb-3">
          Paste a public playlist URL and every track's title + artist will land here automatically. Add moods + notes after.
        </p>
        <div className="flex flex-col md:flex-row gap-2">
          <input
            value={ytUrl}
            onChange={(e) => setYtUrl(e.target.value)}
            placeholder="https://music.youtube.com/playlist?list=..."
            data-testid="admin-yt-url"
            className="wwr-input flex-1 rounded-xl px-4 py-2.5 text-sm"
          />
          <button
            onClick={importFromYT}
            disabled={importing}
            data-testid="admin-yt-import-btn"
            className="btn-primary px-5 py-2.5 text-sm inline-flex items-center gap-2 disabled:opacity-60"
          >
            <Youtube size={14} /> {importing ? "Importing…" : "Import"}
          </button>
        </div>
        <label className="mt-3 inline-flex items-center gap-2 text-xs text-neutral-400 cursor-pointer">
          <input
            type="checkbox"
            checked={ytClearFirst}
            onChange={(e) => setYtClearFirst(e.target.checked)}
            data-testid="admin-yt-clear"
            className="accent-teal-500"
          />
          Replace existing tracks (removes all current tracks first)
        </label>
      </div>

      {/* CSV export / import */}
      <div className="card-surface p-6 mb-6" data-testid="admin-csv-panel">
        <h2 className="font-display text-xl mb-2 flex items-center gap-2"><Download size={16} /> Spreadsheet workflow</h2>
        <p className="text-xs text-neutral-500 mb-3">
          Download all tracks as CSV → open in Excel or Google Sheets → fill in <code className="text-teal-300/80">mood</code>, <code className="text-teal-300/80">note</code>, <code className="text-teal-300/80">is_sample</code>, <code className="text-teal-300/80">paired_toolkits</code> columns → upload back to update everything at once.
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={downloadCsv}
            data-testid="admin-csv-download"
            className="btn-ghost px-5 py-2 text-sm inline-flex items-center gap-2"
          >
            <Download size={14} /> Download CSV
          </button>
          <label className="btn-ghost px-5 py-2 text-sm inline-flex items-center gap-2 cursor-pointer">
            <Upload size={14} /> Upload CSV
            <input type="file" accept=".csv,text/csv" onChange={uploadCsv} data-testid="admin-csv-upload" className="hidden" />
          </label>
        </div>
      </div>

      {/* Add one */}
      <div className="card-surface p-6 mb-6" data-testid="admin-add-form">
        <h2 className="font-display text-xl mb-4 flex items-center gap-2"><Plus size={16} /> Add a track</h2>
        <div className="grid md:grid-cols-2 gap-3 mb-3">
          <input
            value={newTrack.title}
            onChange={(e) => setNewTrack({ ...newTrack, title: e.target.value })}
            placeholder="Song title"
            data-testid="admin-track-title"
            className="wwr-input rounded-xl px-4 py-2.5 text-sm"
          />
          <input
            value={newTrack.artist}
            onChange={(e) => setNewTrack({ ...newTrack, artist: e.target.value })}
            placeholder="Artist"
            data-testid="admin-track-artist"
            className="wwr-input rounded-xl px-4 py-2.5 text-sm"
          />
        </div>
        <div className="grid md:grid-cols-[160px_1fr] gap-3 mb-3">
          <select
            value={newTrack.mood}
            onChange={(e) => setNewTrack({ ...newTrack, mood: e.target.value })}
            data-testid="admin-track-mood"
            className="wwr-input rounded-xl px-4 py-2.5 text-sm"
          >
            {MOODS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <input
            value={newTrack.note}
            onChange={(e) => setNewTrack({ ...newTrack, note: e.target.value })}
            placeholder="One-line note (optional) — why this song mattered"
            data-testid="admin-track-note"
            className="wwr-input rounded-xl px-4 py-2.5 text-sm"
          />
        </div>
        <div className="flex flex-wrap items-center gap-6 mb-4">
          <label className="inline-flex items-center gap-2 text-sm text-neutral-300 cursor-pointer" data-testid="admin-track-sample-label">
            <input
              type="checkbox"
              checked={newTrack.is_sample}
              onChange={(e) => setNewTrack({ ...newTrack, is_sample: e.target.checked })}
              data-testid="admin-track-sample"
              className="accent-teal-500"
            />
            <Star size={14} className="text-amber-300" /> Show in free preview
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs uppercase tracking-widest text-neutral-500">Pair with toolkit:</span>
            {PAIRABLE_TOOLKITS.map((t) => (
              <label key={t.id} className="text-xs inline-flex items-center gap-1 text-neutral-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newTrack.paired_toolkits.includes(t.id)}
                  onChange={() => setNewTrack({
                    ...newTrack,
                    paired_toolkits: togglePairing(newTrack.paired_toolkits, t.id),
                  })}
                  data-testid={`admin-track-pair-${t.id}`}
                  className="accent-teal-500"
                />
                {t.label}
              </label>
            ))}
          </div>
        </div>
        <button
          onClick={addTrack}
          disabled={saving}
          data-testid="admin-add-btn"
          className="btn-primary px-5 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-60"
        >
          <Plus size={14} /> Add
        </button>
      </div>

      {/* Bulk import */}
      <div className="card-surface p-6 mb-10" data-testid="admin-bulk-form">
        <h2 className="font-display text-xl mb-2 flex items-center gap-2"><Upload size={16} /> Bulk import</h2>
        <p className="text-xs text-neutral-500 mb-3">
          One track per line — <code className="text-teal-300/80">Title — Artist | mood | note</code>. Mood and note are optional.
          Bulk imports are added without sample-flag or pairings — set those per track below after import.
        </p>
        <textarea
          value={bulkText}
          onChange={(e) => setBulkText(e.target.value)}
          rows={6}
          placeholder="Simple Man — Lynyrd Skynyrd | reflection | For the moments I needed a plainer voice"
          data-testid="admin-bulk-text"
          className="wwr-input w-full rounded-xl px-4 py-2.5 text-sm font-mono resize-none"
        />
        <button
          onClick={bulkImport}
          disabled={saving}
          data-testid="admin-bulk-btn"
          className="btn-ghost mt-3 px-5 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-60"
        >
          <Upload size={14} /> Import
        </button>
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl">Tracks ({tracks.length})</h2>
        {orderDirty && (
          <button
            onClick={saveOrder}
            data-testid="admin-save-order-btn"
            className="btn-primary px-5 py-2 text-sm inline-flex items-center gap-2"
          >
            <Save size={14} /> Save new order
          </button>
        )}
      </div>
      <ul className="space-y-3" data-testid="admin-tracks-list">
        {tracks.map((t, i) => (
          <li
            key={t.id}
            draggable
            onDragStart={() => onDragStart(i)}
            onDragOver={(e) => onDragOver(e, i)}
            onDragEnd={onDragEnd}
            className={`card-surface p-4 cursor-move ${dragIndex === i ? "opacity-50" : ""}`}
            data-testid={`admin-track-row-${t.id}`}
          >
            <TrackRow
              index={i}
              initial={t}
              onSave={updateTrack}
              onDelete={() => deleteTrack(t.id)}
            />
          </li>
        ))}
      </ul>
    </div>
  );
};

const StatCard = ({ label, value, accent }) => (
  <div className={`card-surface p-4 ${accent ? "border-teal-500/40" : ""}`}>
    <div className="text-xs uppercase tracking-widest text-neutral-500">{label}</div>
    <div className="font-display text-2xl">{value}</div>
  </div>
);

const TrackRow = ({ index, initial, onSave, onDelete }) => {
  const [t, setT] = useState({ ...initial, paired_toolkits: initial.paired_toolkits || [] });
  useEffect(() => {
    setT({ ...initial, paired_toolkits: initial.paired_toolkits || [] });
    // eslint-disable-next-line
  }, [initial.id]);

  const togglePair = (id) =>
    setT({ ...t, paired_toolkits: t.paired_toolkits.includes(id) ? t.paired_toolkits.filter((x) => x !== id) : [...t.paired_toolkits, id] });

  return (
    <>
      <div className="grid md:grid-cols-[20px_30px_1fr_1fr_140px_2fr_auto] gap-2 items-center mb-2">
        <GripVertical size={14} className="text-neutral-600" />
        <div className="text-xs text-neutral-500 font-mono">{String(index + 1).padStart(3, "0")}</div>
        <input
          value={t.title}
          onChange={(e) => setT({ ...t, title: e.target.value })}
          className="wwr-input rounded-lg px-3 py-1.5 text-sm"
        />
        <input
          value={t.artist}
          onChange={(e) => setT({ ...t, artist: e.target.value })}
          className="wwr-input rounded-lg px-3 py-1.5 text-sm"
        />
        <select
          value={t.mood}
          onChange={(e) => setT({ ...t, mood: e.target.value })}
          className="wwr-input rounded-lg px-3 py-1.5 text-sm"
        >
          {MOODS.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <input
          value={t.note || ""}
          onChange={(e) => setT({ ...t, note: e.target.value })}
          placeholder="Note (optional)"
          className="wwr-input rounded-lg px-3 py-1.5 text-sm"
        />
        <div className="flex gap-1">
          <button
            onClick={() => onSave(t)}
            className="btn-ghost text-xs px-3 py-1.5 inline-flex items-center gap-1"
            data-testid={`admin-save-${t.id}`}
          >
            <Save size={12} /> Save
          </button>
          <button
            onClick={onDelete}
            className="text-neutral-500 hover:text-red-400 px-2"
            data-testid={`admin-delete-${t.id}`}
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 pl-11 pt-2 border-t border-neutral-800 mt-2">
        <label className="inline-flex items-center gap-2 text-xs text-neutral-300 cursor-pointer">
          <input
            type="checkbox"
            checked={!!t.is_sample}
            onChange={(e) => setT({ ...t, is_sample: e.target.checked })}
            data-testid={`admin-row-sample-${t.id}`}
            className="accent-teal-500"
          />
          <Star size={12} className="text-amber-300" /> Sample
        </label>
        <label className="inline-flex items-center gap-2 text-xs text-neutral-300 cursor-pointer">
          <input
            type="checkbox"
            checked={!!t.is_featured}
            onChange={(e) => setT({ ...t, is_featured: e.target.checked })}
            data-testid={`admin-row-featured-${t.id}`}
            className="accent-teal-500"
          />
          <Award size={12} className="text-teal-300" /> Featured pool
        </label>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest text-neutral-500">Pair:</span>
          {PAIRABLE_TOOLKITS.map((tk) => (
            <label key={tk.id} className="text-xs inline-flex items-center gap-1 text-neutral-400 cursor-pointer">
              <input
                type="checkbox"
                checked={t.paired_toolkits.includes(tk.id)}
                onChange={() => togglePair(tk.id)}
                data-testid={`admin-row-pair-${t.id}-${tk.id}`}
                className="accent-teal-500"
              />
              {tk.label}
            </label>
          ))}
        </div>
      </div>
    </>
  );
};

export default AdminSoundtrack;
